const SHOW_KACHING_SAVE_BADGE = false;

const KACHING_BADGE_ATTR = 'data-kaching-bundle-badge';
const KACHING_BADGE_LIST_ATTR = 'data-kaching-bundle-badge-list';
const VARIANT_SELECT_SELECTOR = '.kaching-bundles__bar-variant-select.kaching-bundles-option, .kaching-bundles__bar-variant-select';

class KachingPriceSync {
  #updateTimer = null;
  #listenersBound = false;
  #initialized = false;

  constructor(config) {
    this.config = config;
  }

  #getPriceList() {
    return document.querySelector('.product-info .price-list');
  }

  #parseMoneyText(text) {
    if (!text) return null;
    const trimmed = text.trim();
    if (!trimmed) return null;

    const match = trimmed.match(/^([^\d]*?)([\d.,]+)(.*)$/);
    if (!match) return null;

    const numPart = match[2];
    let decimalSep = '.';

    if (numPart.includes(',') && numPart.includes('.')) {
      decimalSep = numPart.lastIndexOf(',') > numPart.lastIndexOf('.') ? ',' : '.';
    } else if (numPart.includes(',')) {
      decimalSep = ',';
    }

    const normalized = decimalSep === ','
      ? numPart.replace(/\./g, '').replace(',', '.')
      : numPart.replace(/,/g, '');

    return {
      formatted: trimmed,
      value: parseFloat(normalized) || 0,
      prefix: match[1],
      suffix: match[3]?.trim() || '',
      decimalSep
    };
  }

  #formatAmount(amount, reference) {
    const fixed = amount.toFixed(2);
    const display = reference.decimalSep === ',' ? fixed.replace('.', ',') : fixed;
    return `${reference.prefix}${display}${reference.suffix ? ` ${reference.suffix}` : ''}`;
  }

  #setPriceElementContent(element, label, priceText) {
    let srOnly = element.querySelector('.sr-only');

    if (!srOnly) {
      srOnly = document.createElement('span');
      srOnly.className = 'sr-only';
      element.replaceChildren(srOnly);
    } else {
      for (const node of [...element.childNodes]) {
        if (node !== srOnly) node.remove();
      }
    }

    srOnly.textContent = label;
    element.append(document.createTextNode(priceText));
  }

  #getOrCreateCompareAtPrice(priceList) {
    let compareAtPrice = priceList.querySelector('compare-at-price');

    if (!compareAtPrice) {
      compareAtPrice = document.createElement('compare-at-price');
      compareAtPrice.className = 'text-subdued line-through';
      priceList.querySelector('sale-price')?.after(compareAtPrice);
    }

    return compareAtPrice;
  }

  #getOrCreateKachingBadgeList(priceList) {
    let badgeList = priceList.nextElementSibling;

    if (badgeList?.matches(`.product-info__badge-list[${KACHING_BADGE_LIST_ATTR}]`)) {
      return badgeList;
    }

    if (badgeList?.classList.contains('product-info__badge-list')) {
      let badge = badgeList.querySelector(`on-sale-badge[${KACHING_BADGE_ATTR}]`);

      if (!badge) {
        badge = document.createElement('on-sale-badge');
        badge.className = 'badge badge--on-sale';
        badge.setAttribute(KACHING_BADGE_ATTR, '');
        badge.hidden = true;
        badgeList.prepend(badge);
      }

      badgeList.setAttribute(KACHING_BADGE_LIST_ATTR, '');
      return badgeList;
    }

    badgeList = document.createElement('div');
    badgeList.className = 'product-info__badge-list';
    badgeList.setAttribute(KACHING_BADGE_LIST_ATTR, '');

    const badge = document.createElement('on-sale-badge');
    badge.className = 'badge badge--on-sale';
    badge.setAttribute(KACHING_BADGE_ATTR, '');
    badge.hidden = true;
    badgeList.append(badge);
    priceList.after(badgeList);

    return badgeList;
  }

  #setVariantBadgesHidden(priceList, hidden) {
    const badgeList = priceList.nextElementSibling;
    if (!badgeList?.classList.contains('product-info__badge-list')) return;

    for (const badge of badgeList.querySelectorAll(`on-sale-badge:not([${KACHING_BADGE_ATTR}])`)) {
      badge.hidden = hidden;
    }
  }

  #updateFromSelectedBar() {
    const priceList = this.#getPriceList();
    const selectedBar = document.querySelector('.kaching-bundles__bar--selected');

    if (!priceList?.isConnected || !selectedBar) return;

    const salePrice = priceList.querySelector('sale-price');
    if (!salePrice) return;

    const priceText = selectedBar.querySelector('.kaching-bundles__bar-price')?.textContent.trim() || '';
    const compareText = selectedBar.querySelector('.kaching-bundles__bar-full-price')?.textContent.trim() || '';
    const hasCompare = compareText.length > 0;

    salePrice.className = hasCompare ? 'text-lg text-on-sale' : 'text-lg';
    this.#setPriceElementContent(salePrice, this.config.salePriceLabel, priceText);

    const compareAtPrice = priceList.querySelector('compare-at-price') || (hasCompare ? this.#getOrCreateCompareAtPrice(priceList) : null);

    if (compareAtPrice) {
      if (hasCompare) {
        compareAtPrice.hidden = false;
        compareAtPrice.className = 'text-subdued line-through';
        this.#setPriceElementContent(compareAtPrice, this.config.regularPriceLabel, compareText);
      } else {
        compareAtPrice.hidden = true;
      }
    }

    if (!SHOW_KACHING_SAVE_BADGE) {
      const badgeList = priceList.nextElementSibling;
      const badge = badgeList?.querySelector?.(`on-sale-badge[${KACHING_BADGE_ATTR}]`);
      if (badge) badge.hidden = true;
      this.#setVariantBadgesHidden(priceList, false);
      return;
    }

    const badgeList = this.#getOrCreateKachingBadgeList(priceList);
    const badge = badgeList.querySelector(`on-sale-badge[${KACHING_BADGE_ATTR}]`);
    const parsedPrice = this.#parseMoneyText(priceText);
    const parsedCompare = this.#parseMoneyText(compareText);

    if (!badge || !parsedPrice || !parsedCompare || !hasCompare || parsedCompare.value <= parsedPrice.value) {
      if (badge) badge.hidden = true;
      this.#setVariantBadgesHidden(priceList, true);
      return;
    }

    const savings = this.#formatAmount(parsedCompare.value - parsedPrice.value, parsedPrice);
    badge.textContent = this.config.discountBadgeHtml.replace('@@SAVINGS@@', savings);
    badge.hidden = false;
    this.#setVariantBadgesHidden(priceList, true);
  }

  #scheduleUpdate() {
    clearTimeout(this.#updateTimer);

    requestAnimationFrame(() => this.#updateFromSelectedBar());

    this.#updateTimer = setTimeout(() => this.#updateFromSelectedBar(), 100);
    setTimeout(() => this.#updateFromSelectedBar(), 300);
  }

  #bindProductRerenderListener() {
    const productRerender = document.querySelector('product-rerender[allow-partial-rerender]');
    if (!productRerender) return;

    const formId = productRerender.getAttribute('observe-form');
    const form = formId ? document.forms[formId] : null;
    if (!form) return;

    form.addEventListener('product:rerender', () => {
      this.#scheduleUpdate();
    });
  }

  #bindDelegatedListeners() {
    if (this.#listenersBound) return;

    document.addEventListener('click', (event) => {
      if (!event.target.closest('.kaching-bundles__bar')) return;
      this.#scheduleUpdate();
    }, true);

    document.addEventListener('change', (event) => {
      if (!event.target.matches(VARIANT_SELECT_SELECTOR)) return;
      this.#scheduleUpdate();
    }, true);

    this.#listenersBound = true;
  }

  #observeKachingRoot() {
    const root = document.querySelector('kaching-bundles-block') || document.querySelector('[class*="kaching-bundles"]');
    if (!root) return;

    const observer = new MutationObserver(() => {
      this.#scheduleUpdate();
    });

    observer.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  #init() {
    if (this.#initialized) return;
    if (!document.querySelector('.product-info')) return;

    this.#bindDelegatedListeners();
    this.#bindProductRerenderListener();
    this.#observeKachingRoot();
    this.#scheduleUpdate();
    this.#initialized = true;
  }

  boot() {
    if (document.querySelector('.kaching-bundles__bar')) {
      this.#init();
      return;
    }

    const kachingObserver = new MutationObserver(() => {
      if (!document.querySelector('.kaching-bundles__bar')) return;
      kachingObserver.disconnect();
      this.#init();
    });

    kachingObserver.observe(document.body, { childList: true, subtree: true });
  }

  static initFromConfig() {
    const configElement = document.getElementById('KachingPriceSyncConfig');
    if (!configElement) return;

    const config = JSON.parse(configElement.textContent);
    const instance = new KachingPriceSync(config);
    instance.boot();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => KachingPriceSync.initFromConfig());
} else {
  KachingPriceSync.initFromConfig();
}
